import React, { createContext, useContext, useState, useEffect } from 'react';

// 账户状态类型定义（基于交e保接口规范）
export interface AccountStatus {
  // 基础状态
  isAccountCreated: boolean;        // 是否已开立会员
  isLedgerCreated: boolean;        // 是否已开立登记簿  
  isCardBound: boolean;            // 是否已绑定银行卡
  
  // 会员信息（基于会员维护V2接口）
  memberInfo: {
    mshp_cust_id: string;           // 会员客户号
    plfm_bsn_id: string;           // 平台业务编号
    cust_tp: 'P' | 'B' | 'G';      // 客户类别 P-个人 B-企业 G-个体工商户
    cmpy_nme: string;              // 单位名称
    cmpy_id_tp: string;            // 单位证件种类
    cmpy_id_no: string;            // 单位证件号码
    cmpy_id_due_dt: string;        // 单位证件过期时间
    org_inst_cd: string;           // 组织机构代码
    rgs_addr: string;              // 注册地址
    opr_addr: string;              // 经营地址
    opr_scope: string;             // 经营范围
    bsn_lic_no: string;            // 营业执照号码
    lgl_rprs_nme: string;          // 法定代表人姓名
    lgl_rprs_id_tp: string;        // 法定代表人证件种类
    lgl_rprs_id_no: string;        // 法定代表人证件号码
    lgl_rprs_id_vld_dt: string;    // 法定代表人证件有效期
    lgl_rprs_phone_no: string;     // 法定代表人手机号码
    ath_agnt_nme: string;          // 授权代理人姓名
    ath_agnt_id_tp: string;        // 授权代理人证件种类
    ath_agnt_id_no: string;        // 授权代理人证件号码
    ath_agnt_id_vld_dt: string;    // 授权代理人证件有效期
    ath_agnt_ctzn: string;         // 授权代理人国籍
    ath_agnt_mob_no: string;       // 授权代理人手机号码
    cust_sts: '0' | '1' | '2';     // 客户状态 0-待生效 1-生效 2-注销
    vrfy_sts: '0' | '1' | '2' | '3' | '4'; // 核验状态
    status: 'pending' | 'approved' | 'rejected';
  } | null;
  
  // 登记簿信息（基于登记簿维护V2接口）
  ledgerInfo: {
    rgsn_book_id: string;          // 登记簿编号
    rgsn_book_acct_nme: string;    // 会员登记簿名称
    book_als: string;              // 会员登记簿别名
    rgsn_book_purp: string;        // 登记簿用途
    mshp_rgsn_book_sts: '1' | '3'; // 会员登记簿状态 1-生效 3-失效
    bal: string;                   // 账户余额
    ava_bal: string;               // 可用余额
    status: 'pending' | 'active';
    createTime: string;
  } | null;
  
  // 银行卡信息（基于绑定账户维护接口）
  cardInfo: {
    id: string;
    bind_acct_no: string;          // 绑定账户账号
    bind_acct_no_acct_nme: string; // 绑定账户户名
    our_bank_othr_bank_flg: '0' | '1'; // 绑定账户交行标志 0-交行 1-他行
    bind_acct_bank_no: string;     // 绑定账户行号
    bind_acct_bank_nme: string;    // 绑定账户行名
    bind_sts: '0' | '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8'; // 绑定核验状态
    bind_acc_sts: '0' | '1' | '2'; // 绑定状态 0-流程中 1-可用 2-不可用
    manager_approver_sts: '0' | '1' | '2' | '3' | '4' | '5' | '6'; // 账户绑定审批状态
    actvt_amt?: string;            // 入金激活金额
    status: 'pending' | 'verified' | 'failed';
    bindTime: string;
  }[] | null;
  
  // 账户余额
  balance: string;
}

interface AccountContextType {
  accountStatus: AccountStatus;
  updateAccountStatus: (status: Partial<AccountStatus>) => void;
  refreshAccountStatus: () => Promise<void>;
  resetAccount: () => void;
}

const defaultAccountStatus: AccountStatus = {
  isAccountCreated: true,  // 演示模式：默认已开户
  isLedgerCreated: true,   // 演示模式：默认已开户
  isCardBound: true,       // 演示模式：默认已开户
  memberInfo: {
    mshp_cust_id: 'CUST202509210001',
    plfm_bsn_id: 'PLTF001',
    cust_tp: 'B',
    cmpy_nme: '上海数据技术有限公司',
    cmpy_id_tp: '2011',
    cmpy_id_no: '91310000MA1FL2E15X',
    cmpy_id_due_dt: '20991231',
    org_inst_cd: 'MA1FL2E15X',
    rgs_addr: '上海市浦东新区张江高科技园区科苑路399号',
    opr_addr: '上海市浦东新区张江高科技园区科苑路399号',
    opr_scope: '数据处理和存储服务；软件开发；信息系统集成服务',
    bsn_lic_no: '91310000MA1FL2E15X',
    lgl_rprs_nme: '张明华',
    lgl_rprs_id_tp: '1101',
    lgl_rprs_id_no: '310101198001011234',
    lgl_rprs_id_vld_dt: '20301231',
    lgl_rprs_phone_no: '13800138000',
    ath_agnt_nme: '李晓芳',
    ath_agnt_id_tp: '1101',
    ath_agnt_id_no: '310101198502021234',
    ath_agnt_id_vld_dt: '20301231',
    ath_agnt_ctzn: 'CN',
    ath_agnt_mob_no: '13900139000',
    cust_sts: '1',
    vrfy_sts: '1',
    status: 'approved'
  },
  ledgerInfo: {
    rgsn_book_id: 'LED202509210001',
    rgsn_book_acct_nme: '上海数据技术有限公司会员登记簿',
    book_als: '数据技术登记簿',
    rgsn_book_purp: '13',
    mshp_rgsn_book_sts: '1',
    bal: '287500.00',
    ava_bal: '287500.00',
    status: 'active',
    createTime: '2025-09-20T10:00:00Z'
  },
  cardInfo: [
    {
      id: '1',
      bind_acct_no: '6228480028881234567',
      bind_acct_no_acct_nme: '上海数据技术有限公司',
      our_bank_othr_bank_flg: '0',
      bind_acct_bank_no: '301290000007',
      bind_acct_bank_nme: '交通银行上海分行营业部',
      bind_sts: '1',
      bind_acc_sts: '1',
      manager_approver_sts: '0',
      status: 'verified',
      bindTime: '2025-09-20T10:00:00Z'
    },
    {
      id: '2',
      bind_acct_no: '6217003320041234567',
      bind_acct_no_acct_nme: '上海数据技术有限公司',
      our_bank_othr_bank_flg: '1',
      bind_acct_bank_no: '102100099996',
      bind_acct_bank_nme: '中国工商银行上海市分行',
      bind_sts: '1',
      bind_acc_sts: '1',
      manager_approver_sts: '0',
      status: 'verified',
      bindTime: '2025-09-19T14:30:00Z'
    }
  ],
  balance: '287500.00'
};

const AccountContext = createContext<AccountContextType | undefined>(undefined);

export const useAccount = () => {
  const context = useContext(AccountContext);
  if (!context) {
    throw new Error('useAccount must be used within an AccountProvider');
  }
  return context;
};

interface AccountProviderProps {
  children: React.ReactNode;
}

export const AccountProvider: React.FC<AccountProviderProps> = ({ children }) => {
  const [accountStatus, setAccountStatus] = useState<AccountStatus>(() => {
    // 尝试从localStorage加载状态
    const saved = localStorage.getItem('accountStatus');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return defaultAccountStatus;
      }
    }
    return defaultAccountStatus;
  });

  // 更新账户状态
  const updateAccountStatus = (newStatus: Partial<AccountStatus>) => {
    setAccountStatus(prev => {
      const updated = { ...prev, ...newStatus };
      localStorage.setItem('accountStatus', JSON.stringify(updated));
      return updated;
    });
  };

  // 刷新账户状态（从服务器获取最新状态）
  const refreshAccountStatus = async () => {
    try {
      // 这里应该调用API获取最新状态，目前使用模拟数据
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // 模拟API响应
      const mockStatus: AccountStatus = {
        isAccountCreated: true,
        isLedgerCreated: true,
        isCardBound: true,
        memberInfo: {
          mshp_cust_id: 'CUST202509210001',
          plfm_bsn_id: 'PLTF001',
          cust_tp: 'B',
          cmpy_nme: '上海数据技术有限公司',
          cmpy_id_tp: '2011',
          cmpy_id_no: '91310000MA1FL2E15X',
          cmpy_id_due_dt: '20991231',
          org_inst_cd: 'MA1FL2E15X',
          rgs_addr: '上海市浦东新区张江高科技园区',
          opr_addr: '上海市浦东新区张江高科技园区',
          opr_scope: '数据处理和存储服务',
          bsn_lic_no: '91310000MA1FL2E15X',
          lgl_rprs_nme: '张三',
          lgl_rprs_id_tp: '1101',
          lgl_rprs_id_no: '310101199001011234',
          lgl_rprs_id_vld_dt: '20301231',
          lgl_rprs_phone_no: '13800138000',
          ath_agnt_nme: '李四',
          ath_agnt_id_tp: '1101',
          ath_agnt_id_no: '310101199002021234',
          ath_agnt_id_vld_dt: '20301231',
          ath_agnt_ctzn: 'CN',
          ath_agnt_mob_no: '13900139000',
          cust_sts: '1',
          vrfy_sts: '1',
          status: 'approved'
        },
        ledgerInfo: {
          rgsn_book_id: 'LED202509210001',
          rgsn_book_acct_nme: '上海数据技术有限公司会员登记簿',
          book_als: '数据技术登记簿',
          rgsn_book_purp: '13',
          mshp_rgsn_book_sts: '1',
          bal: '125000.00',
          ava_bal: '125000.00',
          status: 'active',
          createTime: '2025-09-21T10:00:00Z'
        },
        cardInfo: [{
          id: '1',
          bind_acct_no: '6228480028881234567',
          bind_acct_no_acct_nme: '上海数据技术有限公司',
          our_bank_othr_bank_flg: '0',
          bind_acct_bank_no: '301290000007',
          bind_acct_bank_nme: '交通银行上海分行',
          bind_sts: '1',
          bind_acc_sts: '1',
          manager_approver_sts: '0',
          status: 'verified',
          bindTime: '2025-09-20T10:00:00Z'
        }],
        balance: '125000.00'
      };
      
      setAccountStatus(mockStatus);
      localStorage.setItem('accountStatus', JSON.stringify(mockStatus));
    } catch (error) {
      console.error('Failed to refresh account status:', error);
    }
  };

  // 重置账户状态
  const resetAccount = () => {
    setAccountStatus(defaultAccountStatus);
    localStorage.removeItem('accountStatus');
  };

  // 初始化时检查是否需要刷新状态
  useEffect(() => {
    // 如果账户已创建，定期检查状态更新
    if (accountStatus.isAccountCreated) {
      const interval = setInterval(refreshAccountStatus, 30000); // 每30秒检查一次
      return () => clearInterval(interval);
    }
  }, [accountStatus.isAccountCreated]);

  return (
    <AccountContext.Provider
      value={{
        accountStatus,
        updateAccountStatus,
        refreshAccountStatus,
        resetAccount
      }}
    >
      {children}
    </AccountContext.Provider>
  );
};